// Searchable-select: dependency-free, accessible combobox that PROGRESSIVELY
// ENHANCES a native <select>. The <select> stays the single source of truth —
// its options, .value, per-option data-* (e.g. data-id) and .disabled are never
// duplicated. We only render a filterable UI on top and, on pick, set
// select.value + dispatch a real 'change' event so any existing listeners
// (e.g. valuation.js's brand→models cascade) run unchanged.
//
// Because valuation.js fills these selects AT RUNTIME (after init, and again on
// every brand change), we NEVER cache the option list: it's re-read from
// select.options each time the panel opens or the query changes, and a
// MutationObserver mirrors disabled/options changes back into the input.
//
//   import { enhanceSelect, initSearchableSelects } from './searchable-select.js';
//   enhanceSelect(brandSelect, { placeholder: 'Marca', noResults: 'Niciun rezultat' });
//   initSearchableSelects(root); // enhances every <select data-searchable> in root
import { on } from '../lib/dom.js';
import { t } from '../lib/i18n.js';

let UID = 0;

// Case + diacritic-insensitive normalization for substring matching, so
// "Skoda" matches "Škoda" and "citroen" matches "Citroën".
function norm(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // strip combining diacritical marks
}

// Live snapshot of the select's real options, skipping the empty placeholder.
function readOptions(select) {
  return Array.from(select.options)
    .filter((o) => o.value !== '')
    .map((o) => ({ value: o.value, label: o.textContent }));
}

export function enhanceSelect(select, opts = {}) {
  if (!select || select.dataset.enhanced === '1') return null;
  select.dataset.enhanced = '1';

  const id = 'ss-' + (++UID);
  const listId = id + '-list';
  const placeholder = opts.placeholder
    || select.dataset.ph
    || select.getAttribute('aria-label')
    || '';
  const noResultsText = opts.noResults || t('search_no_results', 'Niciun rezultat');

  // ---- DOM ----
  const wrap = document.createElement('div');
  wrap.className = 'ap-sselect';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'ap-sselect__input ap-valuation__select';
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', listId);
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('spellcheck', 'false');
  input.placeholder = placeholder;
  if (select.getAttribute('aria-label')) input.setAttribute('aria-label', select.getAttribute('aria-label'));

  const listbox = document.createElement('ul');
  listbox.className = 'ap-sselect__list';
  listbox.id = listId;
  listbox.setAttribute('role', 'listbox');
  listbox.hidden = true;

  wrap.appendChild(input);
  wrap.appendChild(listbox);

  // Keep the native <select> in the DOM (source of truth) but visually hidden
  // and out of the tab order — screen readers use the combobox above.
  select.classList.add('ap-sselect__native');
  select.setAttribute('tabindex', '-1');
  select.setAttribute('aria-hidden', 'true');
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);

  let open = false;
  let activeIndex = -1;
  let filtered = []; // [{value,label}] currently shown
  let query = '';
  // When the input is focused we show the query; otherwise the current label.
  let editing = false;

  function currentLabel() {
    const o = select.selectedOptions[0];
    return o && o.value ? o.textContent : '';
  }

  // Reflect the select's disabled state onto the input (valuation.js toggles
  // select.disabled as the flow progresses).
  function syncDisabled() {
    input.disabled = select.disabled;
    if (select.disabled) closeList();
  }

  // Push the current selection label into the input (used after pick, and when
  // the select is repopulated / value changes externally).
  function syncInputText() {
    if (editing) return;
    input.value = currentLabel();
  }

  function render() {
    const all = readOptions(select); // LIVE re-read every render
    const q = norm(query);
    filtered = q
      ? all.filter((o) => norm(o.label).indexOf(q) !== -1)
      : all;

    listbox.innerHTML = '';
    if (!filtered.length) {
      const empty = document.createElement('li');
      empty.className = 'ap-sselect__empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = noResultsText;
      listbox.appendChild(empty);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }

    const selectedVal = select.value;
    filtered.forEach((o, i) => {
      const li = document.createElement('li');
      li.className = 'ap-sselect__option';
      li.id = listId + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.dataset.value = o.value;
      li.textContent = o.label;
      if (o.value === selectedVal) {
        li.classList.add('is-selected');
        li.setAttribute('aria-selected', 'true');
      }
      if (i === activeIndex) li.classList.add('is-active');
      listbox.appendChild(li);
    });
    syncActiveAttr();
  }

  function syncActiveAttr() {
    Array.from(listbox.children).forEach((li, i) => {
      li.classList.toggle('is-active', i === activeIndex);
    });
    if (activeIndex >= 0 && listbox.children[activeIndex]) {
      input.setAttribute('aria-activedescendant', listbox.children[activeIndex].id);
      listbox.children[activeIndex].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function openList() {
    if (open || select.disabled) return;
    open = true;
    // Default the active row to the current selection if it's in the filtered set.
    render();
    activeIndex = filtered.findIndex((o) => o.value === select.value);
    if (activeIndex < 0 && filtered.length) activeIndex = 0;
    syncActiveAttr();
    listbox.hidden = false;
    wrap.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  }

  function closeList() {
    if (!open) { editing = false; syncInputText(); return; }
    open = false;
    editing = false;
    query = '';
    listbox.hidden = true;
    wrap.classList.remove('is-open');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    syncInputText();
  }

  // The core contract-preserving pick: set the NATIVE select's value to a real
  // option value, then dispatch a bubbling 'change' so valuation.js's existing
  // on(brand,'change',…) / on(model,'change',…) handlers fire. selectedOptions
  // (and thus selectedOptions[0].dataset.id) resolves because we set .value to
  // an actual option.
  function pick(value) {
    if (value == null) return;
    select.value = value;
    editing = false;
    query = '';
    syncInputText();
    closeList();
    select.dispatchEvent(new Event('change', { bubbles: true }));
    input.focus();
  }

  // ---- Events ----
  on(input, 'focus', () => { if (!select.disabled) openList(); });

  on(input, 'input', () => {
    editing = true;
    query = input.value;
    if (!open) openList();
    activeIndex = -1;
    render();
    if (filtered.length) { activeIndex = 0; syncActiveAttr(); }
  });

  on(input, 'keydown', (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { openList(); return; }
        if (filtered.length) { activeIndex = (activeIndex + 1) % filtered.length; syncActiveAttr(); }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) { openList(); return; }
        if (filtered.length) { activeIndex = (activeIndex - 1 + filtered.length) % filtered.length; syncActiveAttr(); }
        break;
      case 'Enter':
        if (open && activeIndex >= 0 && filtered[activeIndex]) {
          e.preventDefault();
          pick(filtered[activeIndex].value);
        }
        break;
      case 'Escape':
        if (open) { e.preventDefault(); e.stopPropagation(); closeList(); }
        break;
      case 'Tab':
        closeList();
        break;
      default:
        break;
    }
  });

  // Pointer pick: mousedown (not click) so it fires before the input's blur,
  // and preventDefault keeps focus on the input.
  on(listbox, 'mousedown', (e) => {
    const li = e.target.closest('.ap-sselect__option');
    if (!li) return;
    e.preventDefault();
    pick(li.dataset.value);
  });
  // Hover highlights the row (desktop affordance).
  on(listbox, 'mousemove', (e) => {
    const li = e.target.closest('.ap-sselect__option');
    if (!li) return;
    const idx = Array.from(listbox.children).indexOf(li);
    if (idx !== activeIndex) { activeIndex = idx; syncActiveAttr(); }
  });

  // Click-outside closes.
  const onDocPointer = (e) => { if (!wrap.contains(e.target)) closeList(); };
  document.addEventListener('mousedown', onDocPointer);
  document.addEventListener('touchstart', onDocPointer, { passive: true });

  // The select gets repopulated (brand→models), disabled/enabled, or its value
  // set by valuation.js. Mirror all of that back into the combobox. We re-read
  // options lazily on open, but the observer keeps the closed input label and
  // disabled state honest without any coupling to valuation.js.
  const observer = new MutationObserver(() => {
    syncDisabled();
    syncInputText();
    if (open) render();
  });
  observer.observe(select, {
    attributes: true,
    attributeFilter: ['disabled'],
    childList: true, // options added/removed
    subtree: true,
  });
  // Native 'change' can also come from valuation.js's clearSelect (sets value)
  // or programmatic selection — keep the input label in sync either way.
  on(select, 'change', () => { syncInputText(); });

  // Initial paint.
  syncDisabled();
  syncInputText();

  return {
    el: wrap,
    input,
    select,
    refresh() { syncDisabled(); syncInputText(); if (open) render(); },
    open: openList,
    close: closeList,
    destroy() {
      observer.disconnect();
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    },
  };
}

// Enhance every <select data-searchable> found under `root`.
export function initSearchableSelects(root = document) {
  return Array.from(root.querySelectorAll('select[data-searchable]'))
    .map((sel) => enhanceSelect(sel))
    .filter(Boolean);
}
